import { Component, Vue } from "vue-property-decorator";

import { ValidationObserver, ValidationProvider } from "vee-validate";
import "../../../plugins/vee-validate";
import { DataTableHeader } from "vuetify";

import { v4 as uuidv4 } from "uuid";
import { optimizePhotos, uploadImage } from "@/utils/upload-image";
import { fileSizeToMB, validFileSize10MB } from "@/utils/fileSizeToMB";

import ConfirmDlg from "@/components/popup/confirm-dialog/ConfirmDlg.vue";

import { SocialProgramSubscriptionType } from "@/enums/SocialProgramSubscriptionType";
import { PeriodEnum } from "@/enums/PeriodEnum";

import { FacilitySettingsModel } from "@/models/FacilitySettings";
import { SocialProgram } from "@/models/SocialProgram";
import { Calendar } from "@/models/calendar/Calendar";

import ManipulateCalendarEventDialog from "@/views/calender/manipulate-calendar-event-dialog/ManipulateCalendarEventDialog.vue";
import { EventsTypes } from "@/enums/EventsTypes";
import EditEvent from "../../calender/edit-simple-event-dialog/EditEvent.vue";
import EditEventSeries from "../../calender/edit-event-series-dialog/EditEventSeries.vue";
import Loader from "@/components/loader/Loader.vue";
import { getFormattedDateTimeString } from "@/utils/dateTimeFormat";
import GalleryPhotosUploadDlg from "@/views/staff/gallery/GalleryPhotosUploadDlg/GalleryPhotosUploadDlg.vue";
import {GalleryImage} from "@/models/GalleryImage";
import * as firebase from "firebase";
import {waitResizedImage} from "@/utils/wait-resized-image";

@Component({
  components: {
    ValidationProvider,
    ValidationObserver,
    ConfirmDlg,
    ManipulateCalendarEventDialog,
    EditEvent,
    EditEventSeries,
    Loader,
    GalleryPhotosUploadDlg
  }
})
export default class SocialProgramView extends Vue {
  loading = true;
  readonly = true;
  isSaveProgram = false;

  eventsTypes = EventsTypes;
  socialProgram: SocialProgram = null;
  socialProgramSubscriptionType = SocialProgramSubscriptionType;
  periodEnum = PeriodEnum;

  public selectedEvent = null;
  public isOpenEditing = false;
  public isOpenSeriesEditing = false;

  upcomingEventHeaders: DataTableHeader[] = [];
  pastEventHeaders: DataTableHeader[] = []
  financialEpoaApprovalHeaders: DataTableHeader[] = [];

  saveProgramChange(field = null) {
    this.isSaveProgram = true;

    if(field === "lifestyleProgramCategoryId"){
      const categoryName = this.lifestyleProgramCategories.find(c => c.id === this.socialProgram.lifestyleProgramCategoryId)?.name || null
      this.socialProgram.lifestyleProgramCategoryName = categoryName
    }
  }

  backToLifestylePrograms() {
    if (!this.isSaveProgram) {
      this.$router.push({ path: "/lifestyle-programs" });
    } else {
      this.cancelEdit();
    }
  }

  get currentSocialClubPastEvents(): Calendar[] {
    return this.$store.state.socialClubModule.currentSocialClubPastEvents;
  }

  get currentSocialClubUpcomingEvents(): Calendar[] {
    return this.$store.state.socialClubModule.currentSocialClubUpcomingEvents;
  }

  get lifestyleProgramCategories() {
    return this.$store.state.socialClubModule.lifestyleProgramCategories
  }

  get additionalServiceRequestApprovals(): Record<
    string,
    Record<string, string | boolean | Date>
  >[] {
    const serviceRequestApprovals = this.$store.state.additionalServicesModule
      .serviceRequestApprovals;

    return serviceRequestApprovals.reduce((acc, d) => {
      if (Object.keys(acc).includes(d.residentId)) return acc;

      acc[d.residentId] = serviceRequestApprovals.filter(
        g => g.residentId === d.residentId
      );
      return acc;
    }, {});
  }

  get facility(): FacilitySettingsModel {
    return this.$store.state.facilityModule.selectedFacility;
  }

  
  async created() {
    this.upcomingEventHeaders = [
      {
        text: this.$t('SocialProgramView.Event') as string,
        sortable: false,
        value: "imageUrl"
      },
      {
        text: this.$t('SocialProgramView.DateAndTime') as string,
        align: "start",
        sortable: false,
        value: "start"
      },
      {
        text: "",
        align: "start",
        sortable: false,
        value: "id"
      }
    ]

    this.pastEventHeaders = [
      {
        text: this.$t('SocialProgramView.Event') as string,
        sortable: false,
        value: "imageUrl"
      },
      {
        text: this.$t('SocialProgramView.DateAndTime') as string,
        align: "start",
        sortable: false,
        value: "start"
      }
    ];

    this.financialEpoaApprovalHeaders = [
      {
        text: this.$t('GeneralTranslations.Name') as string,
        sortable: false,
        value: "familyHubMemberName"
      },
      {
        text: this.$t('ViewService.ApprovalStatus') as string,
        sortable: false,
        value: "approved"
      }
    ];
    await this.$store.dispatch("getLifestyleProgramCategories");
  }

  beforeDestroy() {
    this.$store.dispatch("clearCurrentSocialProgramEvents");
    this.$store.dispatch("clearCurrentCalenderRecurringEvents");
  }

  async mounted() {
    await this.$store.dispatch("getFacility");

    this.$store
      .dispatch("getSocialClub", this.$route.params.id)
      .then(async () => {
        this.socialProgram = {
          ...this.$store.state.socialClubModule.currentSocialClub
        };

        await this.loadEvents();

        this.loading = false;
      });

  }

  async saveProgram() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isValid = await (this.$refs.form as any).validate()

    if (isValid) {
      await this.$store.dispatch("updateSocialProgram", this.socialProgram);
      this.readonly = true;
      this.isSaveProgram = false;
    }
  }

  cancelEdit() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this.$refs.confirmationModal as any)
      .open(null, `Are you sure you want to cancel the edits?`, false)
      .then(response => {
        if (response && response.agree) {
          this.socialProgram = {
            ...this.$store.state.socialClubModule.currentSocialClub
          };
          this.readonly = true;
          if (this.isSaveProgram) {
            this.$router.push({ path: "/lifestyle-programs" });
          }
        }
      });
  }

  deleteSocialProgram() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this.$refs.confirmationModal as any)
      .open(
        null,
        `Are you sure you want to delete the ${this.socialProgram.name} lifestyle program?`,
        false
      )
      .then(response => {
        if (response && response.agree) {
          this.$store.dispatch("deleteSocialProgram", this.socialProgram);
          this.$router.push("/lifestyle-programs");
        }
      });
  }

  async saveImage(e) {
    const path = `${this.socialProgram.facilityId}/social_programs/coverImages/${uuidv4()}`;
    const file: File | Blob | any = await optimizePhotos((e.target as HTMLInputElement).files[0], '');
    const fileSizeInMegabytes = fileSizeToMB(file?.size);

    if (!validFileSize10MB(fileSizeInMegabytes)) {
      (this.$refs.inputUpload as any).value = null;
      return;
    }

    uploadImage({
      path,
      file,
      onStart: () => {
        this.loading = true;
      },
      onComplete: async storage => {
        const uploadedImageUrl = await storage.snapshot.ref.getDownloadURL();
        const resizedImageRef = await firebase.storage().ref().child(`${path}_200x200`);

        try {
          const resizedImageUrl = await waitResizedImage(10, resizedImageRef);
          this.socialProgram.imageUrl = uploadedImageUrl;
          this.socialProgram.thumbnailUrl = resizedImageUrl;
        } catch (e) {
          this.socialProgram.imageUrl = uploadedImageUrl;
          this.socialProgram.thumbnailUrl = uploadedImageUrl;
        }

        this.loading = false;
        this.saveProgramChange();
      }
    });
  }

  loadEvents() {
    return this.$store.dispatch("getCurrentSocialProgramEvents", {
      socialClubId: this.socialProgram?.id
    });
  }

  loadRecurringEvents(eventId): void {
    this.$store.dispatch("clearCurrentCalenderRecurringEvents");
    this.$store.dispatch("getCalenderRecurringEvents", eventId);
  }

  selectEvent(event) {
    this.selectedEvent = event;
    this.isOpenEditing = true;
  }

  selectRecurringEvent(event) {
    this.selectedEvent = event;
    this.isOpenSeriesEditing = true;
  }

  async onEventUpdate(event) {
      await this.$store.dispatch("updateSingleCalenderEvent", {
        event,
        id: this.socialProgram?.id
      });
      this.isOpenEditing = false;
      this.selectedEvent = null;
      this.loadEvents();
  }

  async onEventSeriesUpdate(eventData) {
    if (!eventData.event.isRecurring) return;

    await this.$store.dispatch("updateCalenderEvent", eventData);
  }

  afterUpdate(updatedEvents) {
    this.isOpenSeriesEditing = false;
    this.selectedEvent = null;
    this.loadRecurringEvents(updatedEvents[0].id);
    this.loadEvents();
  }

  deleteEvent({id, isRecurring, office365EventId}) {
    (this.$refs.confirmationModal as any)
      .open(null, `Are you sure you want to delete event?`, false)
      .then(async response => {
        if (response && response.agree) {
          await this.$store.dispatch("deleteCalenderEvent", {id, isRecurring, office365EventId});
          this.isOpenEditing = false;
          this.isOpenSeriesEditing = false;
          this.selectedEvent = null;

          this.loadEvents();
        }
      });
  }

  deleteEventSeries(parentEventId) {
    (this.$refs.confirmationModal as any)
      .open(null, `Are you sure you want to delete event series?`, false)
      .then(async response => {
        if (response && response.agree) {
          await this.$store.dispatch(
            "deleteCalenderEventSeries", {
            parentEventId: parentEventId,
            deleteUpcomingEvents: false
          }
          );
          this.isOpenEditing = false;
          this.isOpenSeriesEditing = false;
          this.selectedEvent = null;

          this.loadEvents();
        }
      });
  }

  formattedDateTimeString(date: Date, addYears: boolean, addTime: boolean) {
    if(!this.facility) return ''

    return getFormattedDateTimeString(date, this.facility.dateFormat, addTime?this.facility.timeFormat: null, addYears);
  }

  openGalleryPhotos() {
    (this.$refs.GalleryPhotosUploadDlg as any).open();
  }

  uploadGalleryPhoto(image: GalleryImage) {
    this.socialProgram.imageUrl = image.image;
    this.socialProgram.thumbnailUrl = image?.thumbnailUrl ?? image.image;
    this.saveProgramChange();
  }
}
